import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Calendar, DollarSign, Clock, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { MOCK_PROJECTS, MOCK_APPOINTMENTS, MOCK_INVOICES, MOCK_QUOTES } from '../../mock';

const CustomerDashboard: React.FC = () => {
  const { user } = useAppStore();

  const myProjects = MOCK_PROJECTS.slice(0, 3);
  const myAppointments = MOCK_APPOINTMENTS.slice(0, 2);
  const myInvoices = MOCK_INVOICES.slice(0, 3);
  const myQuotes = MOCK_QUOTES.slice(0, 2);

  return (
    <div>
      {/* Welcome */}
      <div style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)', borderRadius: '1.25rem', padding: '2rem', marginBottom: '2rem', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: '-2rem', top: '-2rem', width: '200px', height: '200px', borderRadius: '50%', background: 'rgb(249 115 22 / 0.08)', pointerEvents: 'none' }} />
        <h2 style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: '0.375rem' }}>Welcome back, {user?.name?.split(' ')[0]}! 👋</h2>
        <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.9rem' }}>Here's a summary of your active projects and upcoming appointments.</p>
        <Link to="/portal/requests" className="btn-primary" style={{ marginTop: '1.25rem', display: 'inline-flex' }}>
          New Service Request <ArrowRight size={16} />
        </Link>
      </div>

      {/* Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {[
          { label: 'Active Projects', value: '2', icon: <Clock size={20} color="#f97316" />, color: '#fff7ed', borderColor: 'rgb(249 115 22 / 0.2)' },
          { label: 'Open Quotes', value: '2', icon: <FileText size={20} color="#3b82f6" />, color: '#eff6ff', borderColor: 'rgb(59 130 246 / 0.2)' },
          { label: 'Upcoming Appts', value: '2', icon: <Calendar size={20} color="#8b5cf6" />, color: '#f5f3ff', borderColor: 'rgb(139 92 246 / 0.2)' },
          { label: 'Unpaid Invoices', value: '2', icon: <DollarSign size={20} color="#ef4444" />, color: '#fef2f2', borderColor: 'rgb(239 68 68 / 0.2)' },
        ].map(stat => (
          <div key={stat.label} style={{ background: stat.color, border: `1px solid ${stat.borderColor}`, borderRadius: '1rem', padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
            <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.625rem', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{stat.icon}</div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a' }}>{stat.value}</div>
              <div style={{ fontSize: '0.75rem', color: '#475569', fontWeight: 500 }}>{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Active Projects */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Active Projects</h3>
            <Link to="/portal/projects" style={{ fontSize: '0.8rem', color: '#f97316', fontWeight: 600 }}>View All</Link>
          </div>
          {myProjects.map(p => (
            <div key={p.id} style={{ padding: '0.875rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <div>
                  <p style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', margin: 0 }}>{p.title}</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>{p.projectNumber}</p>
                </div>
                <span className={`badge ${p.status === 'completed' ? 'badge-success' : p.status === 'in_progress' ? 'badge-info' : 'badge-warning'}`}>
                  {p.status.replace('_', ' ')}
                </span>
              </div>
              <div style={{ background: 'var(--border-color)', borderRadius: '9999px', height: '6px', overflow: 'hidden' }}>
                <div style={{ background: 'linear-gradient(90deg, #f97316, #ea580c)', height: '100%', width: `${p.progress}%`, borderRadius: '9999px', transition: 'width 0.5s' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                <span>{p.progress}% complete</span>
                <span>Due: {p.endDate || 'TBD'}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Upcoming Appointments */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Upcoming Appointments</h3>
            <Link to="/portal/appointments" style={{ fontSize: '0.8rem', color: '#f97316', fontWeight: 600 }}>View All</Link>
          </div>
          {myAppointments.map(appt => (
            <div key={appt.id} style={{ padding: '0.875rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                <span className="badge badge-purple" style={{ textTransform: 'capitalize' }}>{appt.type.replace('_', ' ')}</span>
                <span className={`badge ${appt.status === 'confirmed' ? 'badge-success' : 'badge-warning'}`}>{appt.status}</span>
              </div>
              <p style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', margin: '0.375rem 0 0.25rem' }}>
                {appt.service.replace('-', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>📅 {appt.date} at {appt.timeSlot}</p>
            </div>
          ))}
          <Link to="/portal/appointments" className="btn-secondary" style={{ width: '100%', textAlign: 'center', marginTop: '0.5rem', fontSize: '0.85rem' }}>
            Schedule New Appointment
          </Link>
        </div>

        {/* Recent Invoices */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Recent Invoices</h3>
            <Link to="/portal/invoices" style={{ fontSize: '0.8rem', color: '#f97316', fontWeight: 600 }}>View All</Link>
          </div>
          {myInvoices.map(inv => (
            <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.875rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <p style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', margin: 0 }}>{inv.invoiceNumber}</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>Due: {inv.dueDate}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontWeight: 700, color: inv.status === 'paid' ? '#10b981' : inv.status === 'overdue' ? '#ef4444' : '#f97316', margin: 0 }}>${inv.total.toLocaleString()}</p>
                <span className={`badge ${inv.status === 'paid' ? 'badge-success' : inv.status === 'overdue' ? 'badge-danger' : 'badge-warning'}`}>{inv.status}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Open Quotes */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Open Quotes</h3>
            <Link to="/portal/quotes" style={{ fontSize: '0.8rem', color: '#f97316', fontWeight: 600 }}>View All</Link>
          </div>
          {myQuotes.map(q => (
            <div key={q.id} style={{ padding: '0.875rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                <p style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', margin: 0 }}>{q.quoteNumber}</p>
                <span className={`badge ${q.status === 'accepted' ? 'badge-success' : q.status === 'sent' ? 'badge-info' : 'badge-warning'}`}>{q.status}</span>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>{q.service}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Valid until {q.validUntil}</span>
                <span style={{ fontWeight: 800, color: '#f97316' }}>${q.total.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`@media(max-width:768px){div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}}`}</style>
    </div>
  );
};

export default CustomerDashboard;
